import React, { useEffect, useState, useRef } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import cn from 'classnames'
import Cleave from 'cleave.js/react'
import zxcvbn from 'zxcvbn'
import ReCAPTCHA from 'react-google-recaptcha'
import moment from 'moment'

import UserUtils from '@utils/UserUtils'
import useUser from '@hooks/useUser'
import Api from '@api'
import { ClientResponseType, TosType } from '@custom-types/Auth'
import { ApiErrorResponseType, UserSession } from '@custom-types/common'
import { ParsedUrlQuery } from 'querystring'

const Register = () => {
  const { user, mutateUser } = useUser()
  const router = useRouter();
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [routerQuery, setRouterQuery] = useState<ParsedUrlQuery>();
  const [isInvited, setIsInvited] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>();
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string>();
  const [mobile, setMobile] = useState<string>('');
  const [mobileError, setMobileError] = useState<string>();
  const [password, setPassword] = useState<string>('');
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<number>(-1);
  const [tosAgree, setTosAgree] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>();
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let query: ParsedUrlQuery = {};
    if (router) {
      query = router.query;
      setRouterQuery(query);
      const {account_invite} = query;
      if (account_invite) {
        setIsInvited(true);
      }
    }

    if (user) {
      console.log('user', user);
      if (user.isLoggedIn) {
        if (user.settings) {
          if (user.settings.livemode) {
            router.replace({ pathname: '/dashboard', query });
          } else {
            router.replace({ pathname: '/test/dashboard', query });
          }
        } else {
          router.replace({ pathname: '/test/dashboard', query });
        }
      }
    }
  }, [user, router]);

  useEffect(() => {
    if (email && !emailError && name && mobile && !mobileError && password && passwordStrength > 2 && tosAgree) {
      setSubmitEnabled(true);
    } else {
      setSubmitEnabled(false);
    }
  }, [email, name, mobile, password, tosAgree])

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (!!value) {
      if (!UserUtils.isValidEmail(value)) {
        setEmailError('Please enter a valid email.');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  }

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }

  interface ChangeEvent<T> extends React.ChangeEvent<T> {
    target: { rawValue: string } & EventTarget & T;
  }

  const onMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.rawValue;
    setMobile(value);
    if (!!value) {
      if (!UserUtils.isValidMobile(value)) {
        setMobileError('Please enter a valid mobile number.');
      } else {
        setMobileError('');
      }
    } else {
      setMobileError('');
    }
  }

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (value.length >= 10) {
      const strength = zxcvbn(value, [name, email]);
      //console.log(strength);
      //console.log('strength', strength.score);
      setPasswordStrength(strength.score);
    } else {
      setPasswordStrength(-1);
    }
  }
  
  const onTogglePasswordVisible = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setPasswordVisible(!passwordVisible);
  }

  const onTosChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTosAgree(e.target.checked);
  }

  const onRegisterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    await onRegister();
  }

  const onRegisterClicked = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    await onRegister();
  }

  const onRegister = async () => {
    if (submitEnabled) {
      setLoading(true);
      let tos: TosType = {ip: null, date_accepted: moment().unix(), user_agent: null, location: null}
      const client: ClientResponseType = await Api.fetchClientInfo();
      if (client) {
        tos.ip = client.ip;
        tos.user_agent = client.uag;
        tos.location = client.loc;
      }

      let recaptcha = await captchaRef?.current?.executeAsync();
      captchaRef?.current?.reset();
      console.log('captcha', recaptcha);

      if (recaptcha) {
        const response = await Api.register({
          email,
          name,
          mobile_number: `63${mobile}`,
          password,
          tos,
          recaptcha
        });
        
        console.log('register', response);
        if (response.ok) {
          const verifyEmailRes = await Api.sendVerificationEmail(email);
          if (!verifyEmailRes.ok) {
            console.log('send_email_error', verifyEmailRes.data)
          }
          
          recaptcha = await captchaRef?.current?.executeAsync();
          captchaRef?.current?.reset();

          if (recaptcha) {
            const loginRes = await Api.login({
              email,
              password,
              recaptcha
            });
            if (loginRes.ok) {
              console.log('login', loginRes.data);
              const user: UserSession = loginRes.data;
              await mutateUser(user);
              //setLoading(false);
              
              if (user.settings) {
                if (user.settings.livemode) {
                  router.replace({ pathname: '/dashboard', query: routerQuery });
                } else {
                  router.replace({ pathname: '/test/dashboard', query: routerQuery });
                }
              } else {
                router.replace({ pathname: '/test/dashboard', query: routerQuery });
              }
            }
          }
        } else {
          const error: ApiErrorResponseType = response.data;
          if (error.errors) {
            const {errors} = error;
            if (errors.name) setNameError(errors.name[0]);
            else if (errors.email) setEmailError(errors.email[0]);
            else if (errors.mobile_number) setMobileError(errors.mobile_number[0]);
          } else {
            if (error.message) {
              setSubmitError(error.message);
            } else {
              setSubmitError('Something went wrong. Please try again.')
            }
          }
        }
        /* const user = await mutateUser(
          
        ) */
      }

      setLoading(false);
    }
  }

  return (
    <div className="absolute inset-0 m-0">
      <Head>
        <title>AUB PayMate: Register</title>
        <link rel="stylesheet" href="/css/auth.css"/>
      </Head>
      <div className="w-full h-full">
        <div className="bg-magpie-500 min-h-full h-full flex flex-col">
          {/* Background */}
          <div className="mobile:relative">
            <div className="block desktop:hidden fixed overflow-hidden w-full h-screen">
              <div className="transform -skew-y-12">
                <div className="absolute left-0 right-0 -top-75 h-128 bg-white"></div>
              </div>
            </div>
            <div className="hidden desktop:block fixed left-0 right-0 bottom-0 -top-64 z-0 transform -skew-y-12">
              <div className="absolute left-0 right-0 -top-250 origin-0-50 bg-white h-425"></div>
            </div>
          </div>
          {/* Content */}
          <div className="my-0 mx-4 z-0 relative flex flex-row h-full pt-7 justify-center desktop:pt-14 desktop:justify-between ultraWide:mx-auto ultraWide:w-270">
            <div className="hidden desktop:flex desktop:flex-col">
              <div className="ml-4 mr-1/8">
                <div className="pt-18"></div>
                <div className="pb-10">
                  <div className="pt-1">
                    <img className="h-10 w-auto" src="/images/aub_paymate.png" alt="AUB PayMate" />
                  </div>
                </div>
                <div className="pointer-events-none">
                  <div className="flex flex-col justify-start flex-nowrap -ml-6 -mt-6">
                    <div className="mt-6 ml-6 pointer-events-auto">
                      <div className="flex items-baseline flex-row relative">
                        <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                          <div className="pointer-events-none">
                            <div className="flex items-baseline flex-row justify-start flex-nowrap -ml-3 -mt-3">
                              <div className="mt-3 ml-3 pointer-events-auto">
                                <div className="text-magpie-500">
                                  <div aria-hidden="true" className="flex -mt-0.5 transform translate-y-1">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 ml-3 pointer-events-auto">
                                <div className="pointer-events-none">
                                  <div className="flex flex-col justify-start flex-nowrap -ml-1 -mt-1">
                                    <div className="mt-1 ml-1 pointer-events-auto">
                                      <span className="block text-gray-900 text-base font-medium leading-6 proportional-nums whitespace-normal">Get started quickly</span>
                                    </div>
                                    <div className="mt-1 ml-1 pointer-events-auto">
                                    <span className="block text-sm proportional-nums whitespace-normal -mt-px">Create your account for faster coordination.</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 ml-6 pointer-events-auto">
                      <div className="flex items-baseline flex-row relative">
                        <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                          <div className="pointer-events-none">
                            <div className="flex items-baseline flex-row justify-start flex-nowrap -ml-3 -mt-3">
                              <div className="mt-3 ml-3 pointer-events-auto">
                                <div className="text-magpie-500">
                                  <div aria-hidden="true" className="flex -mt-0.5 transform translate-y-1">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 ml-3 pointer-events-auto">
                                <div className="pointer-events-none">
                                  <div className="flex flex-col justify-start flex-nowrap -ml-1 -mt-1">
                                    <div className="mt-1 ml-1 pointer-events-auto">
                                      <span className="block text-gray-900 text-base font-medium leading-6 proportional-nums whitespace-normal">Integrate AUB PayMate digital payments API in your e-commerce/online channels</span>
                                    </div>
                                    <div className="mt-1 ml-1 pointer-events-auto">
                                    <span className="block text-sm proportional-nums whitespace-normal -mt-px">We will work with you every step of the way to make this happen.</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 ml-6 pointer-events-auto">
                      <div className="flex items-baseline flex-row relative">
                        <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                          <div className="pointer-events-none">
                            <div className="flex items-baseline flex-row justify-start flex-nowrap -ml-3 -mt-3">
                              <div className="mt-3 ml-3 pointer-events-auto">
                                <div className="text-magpie-500">
                                  <div aria-hidden="true" className="flex -mt-0.5 transform translate-y-1">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 ml-3 pointer-events-auto">
                                <div className="pointer-events-none">
                                  <div className="flex flex-col justify-start flex-nowrap -ml-1 -mt-1">
                                    <div className="mt-1 ml-1 pointer-events-auto">
                                      <span className="block text-gray-900 text-base font-medium leading-6 proportional-nums whitespace-normal">Avail of other services</span>
                                    </div>
                                    <div className="mt-1 ml-1 pointer-events-auto">
                                    <span className="block text-sm proportional-nums whitespace-normal -mt-px">Coming soon!</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-grow flex-shrink"></div>
              <div className="relative py-6 pl-5">
                <div className="pointer-events-none">
                  <div className="flex flex-row justify-start flex-nowrap -ml-2 -mt-2">
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <a className="text-sm text-gray-200 font-medium hover:text-white" href="https://magpie.im" target="_self">Powered by Magpie</a>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <span className="text-gray-50">·</span>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <a className="text-sm text-gray-200 font-medium hover:text-white" href="https://magpie.im/terms.html" target="_self">Terms &amp; conditions</a>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <span className="text-gray-50">·</span>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <a className="text-sm text-gray-200 font-medium hover:text-white" href="https://magpie.im/privacy.html" target="_self">Privacy policy</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Registration Form */}
            <div className="flex flex-col w-96 desktop:w-1/2 desktop:min-w-1/2">
              <div className="block pl-5 pb-6 desktop:hidden">
                <img className="h-10 w-auto" src="/images/aub_paymate.png" alt="AUB PayMate" />
              </div>
              <div className="flex-shrink-0 bg-white overflow-hidden shadow-card-lg rounded">
                <div className="py-8 px-0 desktop:py-14 desktop:px-12">
                  <div>
                    <div className="pb-5 px-5">
                      <h1 className="text-2xl font-bold tracking-tight proportional-nums">Create your AUB PayMate account</h1>
                    </div>
                    {
                      isInvited ? (
                        <div>
                          <div className="bg-white mx-5 mb-0.5 overflow-hidden shadow-keyline rounded">
                            <div className="bg-magpie-50 py-4 px-5">
                              <span className="text-sm inline-block text-body leading-5 proportional-nums whitespace-normal">
                                You've been invited to join a team on AUB PayMate. Create an account to log in to your new dashboard.
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : null
                    }
                    <form onSubmit={onRegisterSubmit} className="min-w-0 m-0 p-0 border-0" noValidate>
                      <div>
                        <div>
                          <div className="min-h-26 pt-1">
                            <div className="px-4 pt-1 pb-0">
                              <div className="pb-2 max-w-2xl w-full">
                                <label htmlFor="email" className="text-gray-900 text-sm inline font-medium proportional-nums">Email</label>
                              </div>
                              <div>
                                <div className="w-full max-w-2xl pointer-events-none">
                                  <div className="-ml-4 -mt-4">
                                    <div className="mt-4 ml-4 pointer-events-auto">
                                      <input id="email" name="email" type="email" autoComplete="email" autoCapitalize="none" aria-invalid={!!emailError} className={cn("inline-flex w-full max-w-full cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border focus:ring focus:ring-opacity-50", {
                                        "border-gray-200 focus:outline-none focus:border-magpie-300 focus:ring-magpie-200": !emailError,
                                        "border-red-500 focus:outline-none focus:border-red-500 focus:ring-red-300": emailError
                                      })} value={email} onChange={(e) => onEmailChange(e)} />
                                      <div className={cn("block pt-2 transition-opacity duration-250", {
                                        "opacity-0": !emailError,
                                        "opacity-100": emailError
                                      })}>
                                        <div role="alert" className="flex flex-row">
                                          <div className="flex items-baseline flex-row pr-1 relative">
                                            <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                                              <div className="flex text-red-500">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                              </div>
                                            </div>
                                          </div>
                                          <span className="text-red-500 inline text-xs proportional-nums whitespace-normal">{emailError}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="min-h-25">
                          <div>
                            <div className="flex items-stretch flex-col px-5 pt-1 pb-0">
                              <div className="pb-2 w-full max-w-2xl">
                                <label htmlFor="name" className="text-gray-900 text-sm inline font-medium proportional-nums">Full name</label>
                              </div>
                              <div>
                                <div className="w-full max-w-2xl pointer-events-none">
                                  <div className="-ml-4 -mt-4">
                                    <div className="mt-4 ml-4 pointer-events-auto">
                                      <input id="name" name="name" type="text" autoComplete="name" aria-invalid={!name} className="inline-flex w-full max-w-full cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border border-gray-200 focus:outline-none focus:border-magpie-300 focus:ring focus:ring-magpie-200 focus:ring-opacity-50" value={name} onChange={(e) => onNameChange(e)} />
                                      <div className={cn("block pt-2 transition-opacity duration-250", {
                                        "opacity-0": !nameError,
                                        "opacity-100": nameError
                                      })}>
                                        <div role="alert" className="flex flex-row">
                                          <div className="flex items-baseline flex-row pr-1 relative">
                                            <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                                              <div className="flex text-red-500">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                              </div>
                                            </div>
                                          </div>
                                          <span className="text-red-500 inline text-xs proportional-nums whitespace-normal">{nameError}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="min-h-25">
                          <div>
                            <div className="flex items-stretch flex-col px-5 pt-1 pb-0">
                              <div className="pb-2 w-full max-w-2xl">
                                <label htmlFor="phone" className="text-gray-900 text-sm inline font-medium proportional-nums">Mobile number</label>
                              </div>
                              <div>
                                <div className="w-full max-w-2xl pointer-events-none">
                                  <div className="-ml-4 -mt-4">
                                    <div className="mt-4 ml-4">
                                      <div className="relative pointer-events-auto">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                          <span className="text-gray-900 text-sm">
                                            +63
                                          </span>
                                        </div>
                                        {/* <input id="phone" name="phone" type="tel" autoComplete="tel" maxLength={12} placeholder="917 123 4567" aria-invalid="false" className={cn("inline-flex w-full max-w-full pl-14 cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border placeholder-gray-300 focus:outline-none focus:ring focus:ring-opacity-50 transition duration-250 ease-in-out", {
                                          "border-gray-200 focus:border-magpie-300 focus:ring-magpie-200": !mobileError,
                                          "border-red-500 focus:border-red-500 focus:ring-red-300": mobileError
                                        })} value={mobile} onChange={(e) => onMobileChange(e)} /> */}
                                        <Cleave options={{blocks: [3,3,4], delimiter: ' ', numericOnly: true}} id="phone" name="phone" type="tel" autoComplete="tel" placeholder="917 123 4567" aria-invalid={!!mobileError} className={cn("inline-flex w-full max-w-full pl-14 cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border placeholder-gray-300 focus:outline-none focus:ring focus:ring-opacity-50 transition duration-250 ease-in-out", {
                                          "border-gray-200 focus:border-magpie-300 focus:ring-magpie-200": !mobileError,
                                          "border-red-500 focus:border-red-500 focus:ring-red-300": mobileError
                                        })} onChange={(e) => onMobileChange(e)} />
                                      </div>
                                      <div className={cn("block pt-2 transition-opacity duration-250", {
                                        "opacity-0": !mobileError,
                                        "opacity-100": mobileError
                                      })}>
                                        <div role="alert" className="flex flex-row">
                                          <div className="flex items-baseline flex-row pr-1 relative">
                                            <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                                              <div className="flex text-red-500">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                              </div>
                                            </div>
                                          </div>
                                          <span className="text-red-500 inline text-xs proportional-nums whitespace-normal">{mobileError}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3 min-h-27">
                          <div className="flex items-stretch flex-col px-5 pt-1 pb-0">
                            <div className="pb-2 w-full max-w-2xl">
                              <label htmlFor="password" className="text-gray-900 text-sm inline font-medium proportional-nums">Password</label>
                            </div>
                            <div>
                              <div className="w-full max-w-2xl pointer-events-none">
                                <div className="-ml-4 -mt-4">
                                  <div className="pt-4 ml-4 pointer-events-auto">
                                    <div>
                                      <div className="relative">
                                        <input id="password" name="password" type={ passwordVisible ? "text" : "password"} autoComplete="new-password" aria-invalid={passwordStrength < 3} className={cn("inline-flex w-full max-w-full pr-14 cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border focus:outline-none focus:ring focus:ring-opacity-50 transition duration-250 ease-in-out", {
                                          "border-gray-200 focus:border-magpie-300 focus:ring-magpie-200": passwordStrength === -1 || passwordStrength > 2,
                                          "border-red-500 focus:border-red-500 focus:ring-red-300": passwordStrength > -1 && passwordStrength < 3
                                        })} value={password} onChange={(e) => onPasswordChange(e)} />
                                        <button disabled={!password} type="button" className={cn("flex absolute inset-y-0 right-0 pr-3 items-center text-gray-400 hover:text-gray-700 transition duration-250 ease-in-out focus:outline-none", {
                                          "opacity-100 cursor-pointer": password,
                                          "opacity-0 cursor-default": !password
                                        })} onClick={(e) => onTogglePasswordVisible(e)}>
                                          {
                                            passwordVisible ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"></path><path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"></path></svg> : <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path></svg>
                                          }
                                        </button>
                                      </div>
                                      <div className="flex flex-col">
                                        <div className="basis-11">
                                          <div className={cn("min-h-4.5 transition-opacity duration-200 leading-3 pt-2", {
                                            "opacity-0": !password,
                                            "opacity-100": password
                                          })}>
                                            {/* Password strength description */}
                                            <span className={cn("inline text-xs proportional-nums whitespace-normal", {
                                              "text-gray-700": passwordStrength === -1 || passwordStrength > 3,
                                              "text-red-500": passwordStrength > -1 && passwordStrength < 3,
                                              "text-yellow-500": passwordStrength === 3,
                                              "text-green-600": passwordStrength > 3
                                            })}>
                                              {
                                                passwordStrength === -1 && <span>Your password needs to be at least 10 characters. Include multiple words and phrases to make it secure.</span>
                                              }
                                              {
                                                passwordStrength > -1 && passwordStrength < 3 && <span>Your password is not strong enough. Avoid using a single sequence of characters (e.g., abcdefgh or 12345678).</span>
                                              }
                                              {
                                                passwordStrength === 3 && <span>Your password works, but could be stronger. Try making it longer. </span>
                                              }
                                              {
                                                passwordStrength > 3 && <span>Nice work! Your password is good.</span>
                                              }
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <input type="hidden" name="io_blackbox" value="A_TOKEN" />
                        <div className="px-5 pt-1 pb-0">
                          <div>
                            <div className="w-full max-w-2xl pointer-events-none">
                              <div className="flex flex-col justify-start flex-nowrap -ml-4 -mt-4">
                                <div className="pt-4 pl-4 pointer-events-auto">
                                  <div className="pb-8 -ml-px">
                                    <div className="flex flex-row relative">
                                      <div className="flex flex-row justify-start flex-1-auto leading-0">
                                        <input type="checkbox" id="tos" name="tos" aria-invalid={!tosAgree} className="rounded border-gray-200 text-magpie-500 shadow-sm focus:border-magpie-300 focus:ring focus:ring-magpie-200 focus:ring-opacity-50" checked={tosAgree} onChange={(e) => onTosChecked(e)} />
                                        <div className="pl-2">
                                          <label htmlFor="tos" className="text-xs text-gray-600 inline whitespace-normal">I agree to <a className="text-magpie-600 font-medium no-underline hover:text-magpie-400" href="https://magpie.im/terms.html" target="_blank">Terms and Privacy Policy</a> of this service.</label>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="px-5 pt-0 pb-4">
                          <div>
                            <div className={cn("block pb-2 transition-opacity duration-250", {
                                "opacity-0": !submitError,
                                "opacity-100": submitError
                              })}>
                                <div role="alert" className="flex flex-row">
                                  <div className="flex items-baseline flex-row pr-1 relative">
                                    <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                                      <div className="flex text-red-500">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                      </div>
                                    </div>
                                  </div>
                                  <span className="text-red-500 inline text-xs proportional-nums whitespace-normal">{submitError}</span>
                                </div>
                              </div>
                            <div className="w-full max-w-2xl pointer-events-none">
                              <div className="flex flex-col justify-start flex-nowrap -ml-4 -mt-4">
                                <div className="pt-4 pl-4 pointer-events-auto">
                                  <div className="flex flex-col pt-1">
                                    <button type="submit" disabled={!submitEnabled || loading} className={cn("py-2 px-4 rounded min-h-11 h-full w-full inline-flex text-base leading-7 font-medium cursor-default text-white border-none bg-magpie-500 focus:outline-none transition duration-250 ease-linear focus:ring focus:ring-opacity-50 focus:ring-magpie-200", {
                                      "opacity-60": !submitEnabled || loading,
                                      "opacity-100": submitEnabled && !loading
                                    })} onClick={(e) => onRegisterClicked(e)}>
                                      <div className="flex align-baseline flex-row-reverse w-full relative">
                                        <div className="flex align-baseline flex-row-reverse justify-center w-full leading-0 flex-1-auto">
                                          <span className={cn("block text-white text-base font-medium leading-7 proportional-nums whitespace-nowrap transition duration-250 ease-linear -mt-px", {
                                            "opacity-100": !loading,
                                            "opacity-0": loading
                                          })}>Create account</span>
                                          <div className={cn("spinner-icon block absolute top-1/2 transform -translate-y-1/2 opacity-0 transition-all duration-200 -mt-px", { 'opacity-0 transform -translate-y-1/2': !submitEnabled || submitEnabled, 'opacity-100': loading })}>
                                            <div className="w-5 h-5">
                                              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false">
                                                <ellipse className="origin-center animate-spinner-spin" cx="12" cy="12" rx="10" ry="10" style={{ stroke: 'rgb(255, 255, 255)' }}></ellipse>
                                              </svg>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <ReCAPTCHA
                          ref={captchaRef}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY || ''}
                          size="invisible"
                        />
                        <div className="flex justify-center pt-3">
                          <div>
                            <span className="text-sm">Have an account? <Link href={{ pathname: '/login', query: routerQuery }}><a className="text-magpie-600 font-medium no-underline hover:text-magpie-400">Sign in</a></Link></span>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <div className="flex-grow flex-shrink"></div>
              <div className="block relative py-6 px-5 desktop:hidden">
                <div className="pointer-events-none">
                  <div className="flex flex-row justify-center flex-nowrap -ml-2 -mt-2 desktop:justify-start">
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <a className="text-sm text-gray-200 font-medium hover:text-white" href="https://magpie.im" target="_self">Powered by Magpie</a>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <span className="text-gray-50">·</span>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <a className="text-sm text-gray-200 font-medium hover:text-white" href="https://magpie.im/terms.html" target="_self">Privacy &amp; terms</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Banner */}
      <div></div>
    </div>
  )
}

export default Register